import {retenu} from '../../selectors/etat.js';
import {SEUIL_RURAL_HAB} from '../../selectors/population.js';

// PIL-01 lot 9 : agrégations pures du dashboard (courbes hebdomadaires, communes, alertes collecte).
// Source unique : `index.evenementsParPav` (les événements de collecte n'y figurent que bouton Collecte à 1).

const MS_JOUR = 86400000;
const EST_COLLECTE = (e) => e.type === 'COLLECTE' || e.type === 'COLLECTE_IMPOSSIBLE';
const taux = (e) => (e.type === 'COLLECTE' && e.detail && typeof e.detail.tauxRemplissage === 'number' ? e.detail.tauxRemplissage : null);
const kg = (e) => (e.type === 'COLLECTE' ? (e.detail && e.detail.tonnageKg) || 0 : 0);
const moyenne = (valeurs) => (valeurs.length > 0 ? valeurs.reduce((a, b) => a + b, 0) / valeurs.length : null);

const versMs = (iso) => { const [y, m, d] = iso.split('-').map(Number); return Date.UTC(y, m - 1, d); };
const versIso = (ms) => new Date(ms).toISOString().slice(0, 10);

/** Lundi (Y-m-d) de la semaine de `iso`. */
export function lundi(iso) {
    const ms = versMs(iso);
    const jour = new Date(ms).getUTCDay(); // 0 = dimanche
    return versIso(ms - ((jour + 6) % 7) * MS_JOUR);
}

/**
 * Série hebdomadaire sur [du, au] : un point par semaine (x = lundi), y = null pour une semaine sans événement mesuré.
 * `mesure` : 'tonnage' (somme des kg des COLLECTE), 'remplissage' (taux moyen des COLLECTE, déclassés exclus),
 * 'lavages' (nombre de LAVAGE). `evts` : événements déjà retenus sur la plage ; ceux hors [du, au] sont ignorés.
 */
export function serieHebdo(evts, du, au, mesure) {
    const parSemaine = new Map();
    for (const e of evts) {
        if ((du && e.date < du) || (au && e.date > au)) continue;
        let valeur;
        if (mesure === 'tonnage') valeur = e.type === 'COLLECTE' ? kg(e) : undefined;
        else if (mesure === 'remplissage') valeur = taux(e) ?? undefined;
        else if (mesure === 'lavages') valeur = e.type === 'LAVAGE' ? 1 : undefined;
        if (valeur === undefined) continue;
        const sem = lundi(e.date);
        if (!parSemaine.has(sem)) parSemaine.set(sem, []);
        parSemaine.get(sem).push(valeur);
    }
    const debut = du || (parSemaine.size > 0 ? [...parSemaine.keys()].sort()[0] : au);
    const points = [];
    for (let ms = versMs(lundi(debut)); ms <= versMs(au); ms += 7 * MS_JOUR) {
        const x = versIso(ms);
        const v = parSemaine.get(x);
        let y = null;
        if (v) y = mesure === 'remplissage' ? moyenne(v) : v.reduce((a, b) => a + b, 0);
        points.push({x, y});
    }
    return points;
}

function collectesRetenues(pav, index, du, au) {
    return (index.evenementsParPav.get(pav.id) || []).filter((e) => EST_COLLECTE(e) && retenu(e, du, au));
}

/** Tuiles A3 : tonnage total, tonnage moyen par bac, remplissage moyen, déclassés en cours (dernier événement <= au). */
export function syntheseCollecte(pavs, index, du, au) {
    let tonnageKg = 0;
    let nb = 0;
    let nbBacs = 0;
    const tauxTous = [];
    let declassesEnCours = 0;
    let nbEvenements = 0;
    for (const pav of pavs) {
        const evts = collectesRetenues(pav, index, du, au);
        nbEvenements += evts.length;
        if (evts.length > 0 && evts[evts.length - 1].type === 'COLLECTE_IMPOSSIBLE') declassesEnCours++;
        for (const e of evts) {
            if (e.type !== 'COLLECTE') continue;
            nb++;
            nbBacs += (e.detail && e.detail.nbBacs) || 0;
            tonnageKg += kg(e);
            const t = taux(e);
            if (t !== null) tauxTous.push(t);
        }
    }
    return {nbEvenements, tonnageKg, nbCollectes: nb, tonnageMoyenKg: nbBacs > 0 ? tonnageKg / nbBacs : null, remplissageMoyen: moyenne(tauxTous), declassesEnCours};
}

/**
 * Tableau par commune : PAV de la sélection, tonnage (kg), remplissage moyen (moyenne des collectes, déclassés
 * exclus), déclassés. Tri par remplissage croissant (sans mesure en dernier), puis ligne `territoire` (cle 'territoire').
 */
export function collecteParCommune(pavs, index, du, au) {
    const communes = new Map();
    const ligne = (cle, libelle) => ({cle, commune: libelle, pav: 0, tonnageKg: 0, tauxListe: [], declasses: 0, population: 0, auDessusSeuil: 0});
    const territoire = ligne('territoire', 'Territoire');
    for (const pav of pavs) {
        const cibles = [territoire];
        if (pav.communeCle != null) {
            if (!communes.has(pav.communeCle)) communes.set(pav.communeCle, ligne(pav.communeCle, pav.communeLibelle));
            cibles.push(communes.get(pav.communeCle));
        }
        for (const c of cibles) {
            c.pav++;
            c.population += pav.populationAllouee || 0; // lot 12 : Σ population allouée (PAV non géolocalisé : 0)
            if (pav.groupe === 'extra' && pav.populationAllouee150 > SEUIL_RURAL_HAB) c.auDessusSeuil++; // lot 13 : grille rurale, PAV du parc aérien de plus de 250 hab. alloués à 150 m (rayon fixe)
        }
        for (const e of collectesRetenues(pav, index, du, au)) {
            for (const c of cibles) {
                if (e.type === 'COLLECTE_IMPOSSIBLE') c.declasses++;
                else {
                    c.tonnageKg += kg(e);
                    const t = taux(e);
                    if (t !== null) c.tauxListe.push(t);
                }
            }
        }
    }
    // kg / 100 hab. = tonnage de la période / Σ population allouée des PAV de la commune × 100 ; null sans population allouée.
    const final = (c) => ({
        cle: c.cle, commune: c.commune, pav: c.pav, tonnageKg: c.tonnageKg, remplissage: moyenne(c.tauxListe), declasses: c.declasses,
        populationAllouee: c.population, pavAuDessusSeuilRural: c.auDessusSeuil, kgPour100hab: c.population > 0 ? (c.tonnageKg / c.population) * 100 : null,
    });
    const lignes = [...communes.values()].map(final).sort((a, b) => {
        if (a.remplissage === null || b.remplissage === null) {
            if (a.remplissage === b.remplissage) return a.commune.localeCompare(b.commune);
            return a.remplissage === null ? 1 : -1;
        }
        return a.remplissage - b.remplissage || a.commune.localeCompare(b.commune);
    });
    lignes.push(final(territoire));
    return lignes;
}

export const SEUILS_COLLECTE = {plein: 0.90, sousUtilise: 0.10, nSousUtilise: 3, declassesMois: 2};

/**
 * Alertes collecte à date (indépendantes de la période) sur le parc actif de `pavs` (défaut : tout l'index).
 * - plein : la dernière collecte (déclassée ou non) a un remplissage >= `plein` ;
 * - sousUtilisees : les `nSousUtilise` dernières collectes sont toutes des COLLECTE de remplissage < `sousUtilise` ;
 * - declassesRecurrents : >= `declassesMois` COLLECTE_IMPOSSIBLE sur les 30 derniers jours (]aujourd'hui - 30 j, aujourd'hui]).
 */
export function alertesCollecte(index, aujourdhui, seuils = SEUILS_COLLECTE, pavs = null) {
    const s = {...SEUILS_COLLECTE, ...seuils};
    const debut = versIso(versMs(aujourdhui) - 30 * MS_JOUR);
    const res = {plein: {nb: 0, pavIds: []}, sousUtilisees: {nb: 0, pavIds: []}, declassesRecurrents: {nb: 0, pavIds: []}};
    for (const pav of (pavs || index.pavs)) {
        if (pav.statut === 'RETIRÉ') continue;
        const evts = (index.evenementsParPav.get(pav.id) || []).filter((e) => EST_COLLECTE(e) && e.date <= aujourdhui);
        if (evts.length === 0) continue;
        const derniere = evts[evts.length - 1];
        const t = taux(derniere);
        if (t !== null && t >= s.plein) res.plein.pavIds.push(pav.id);
        const fin = evts.slice(-s.nSousUtilise);
        if (fin.length === s.nSousUtilise && fin.every((e) => taux(e) !== null && taux(e) < s.sousUtilise)) res.sousUtilisees.pavIds.push(pav.id);
        if (evts.filter((e) => e.type === 'COLLECTE_IMPOSSIBLE' && e.date > debut).length >= s.declassesMois) res.declassesRecurrents.pavIds.push(pav.id);
    }
    for (const k of Object.keys(res)) res[k].nb = res[k].pavIds.length;
    return res;
}

/** Descripteurs des 3 alertes collecte (même forme que `tuilesAlertes`) : nb et 5 premières nomenclatures en sous-ligne. */
export function tuilesAlertesCollecte(index, aujourdhui, pavs = null, seuils = SEUILS_COLLECTE) {
    const a = alertesCollecte(index, aujourdhui, seuils, pavs);
    const filtre = {acte: 'collecte', style: 'tonnage', du: '', au: aujourdhui};
    const noms = (ids) => {
        const liste = ids.map((id) => index.pavById.get(id)).filter(Boolean).map((p) => p.nomenclature).sort((x, y) => x.localeCompare(y, 'fr', {numeric: true}));
        return liste.length === 0 ? '—' : `${liste.slice(0, 5).join(', ')}${liste.length > 5 ? '…' : ''}`;
    };
    const s = {...SEUILS_COLLECTE, ...seuils};
    return [
        {id: 'plein', titre: 'Bac plein', nb: a.plein.nb, sousLigne: `dernière collecte ≥ ${Math.round(s.plein * 100)} % · ${noms(a.plein.pavIds)}`, filtre},
        {id: 'sousUtilisees', titre: 'Sous-utilisée', nb: a.sousUtilisees.nb, sousLigne: `${s.nSousUtilise} dernières collectes < ${Math.round(s.sousUtilise * 100)} % · ${noms(a.sousUtilisees.pavIds)}`, filtre},
        {id: 'declassesRecurrents', titre: 'Déclassés récurrents', nb: a.declassesRecurrents.nb, sousLigne: `≥ ${s.declassesMois} déclassés sur 30 jours · ${noms(a.declassesRecurrents.pavIds)}`, filtre},
    ];
}

const champCsv = (v) => `"${(v == null ? '' : String(v)).replace(/"/g, '""')}"`;

/** CSV du tableau par commune (séparateur « ; », BOM UTF-8, tonnage en kg, remplissage en %). */
export function csvCommunes(lignes) {
    const entete = ['commune', 'pav', 'tonnageKg', 'remplissageMoyenPct', 'declasses', 'populationAllouee', 'kgPour100hab', 'pavAuDessus250hab150m'];
    const corps = lignes.map((l) => [l.commune, l.pav, l.tonnageKg, l.remplissage == null ? '' : Math.round(l.remplissage * 1000) / 10, l.declasses, Math.round(l.populationAllouee), l.kgPour100hab == null ? '' : Math.round(l.kgPour100hab * 10) / 10, l.pavAuDessusSeuilRural].map(champCsv).join(';'));
    return `﻿${[entete.map(champCsv).join(';'), ...corps].join('\n')}`;
}
